import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, RefreshCw } from "lucide-react";
import { api } from "../../lib/api";
import { useVendors, usePlants, useErpSystems } from "../../lib/useReference";
import { formatDate } from "../../lib/dateFormat";
import { useDebounce } from "../../lib/useDebounce";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { FieldSelect, FieldInput, DateFormatRadios } from "../../components/ui/Field";
import { ReportTable, type ReportColumn } from "../../components/ui/ReportTable";

interface PoConfirmedRow {
  id: number;
  creationDate: string;
  poNumber: string;
  poLineItem: number;
  status: "unconfirmed" | "confirmed";
  materialNumber: string;
  materialDescription: string;
  originalQty: number;
  unit: string;
  originalDeliveryDate: string;
  etdDate: string | null;
  plantId: number;
  unitPrice: number | null;
  currency: string;
  vendorId: number;
  paymentTerms: string | null;
  mrpControllerNumber: string | null;
  mrpControllerName: string | null;
  regionalBuyer: string | null;
  regionalBuyerName: string | null;
  confirmedQty: number | null;
  confirmedDeliveryDate: string | null;
  confirmedAt: string | null;
  proposalQty: number | null;
  proposalDeliveryDate: string | null;
  proposalApprovedAt: string | null;
  approvedQty: number | null;
  approvedDeliveryDate: string | null;
  confirmationApprovedAt: string | null;
  confirmationSource: "as_is" | "approved_with_change" | null;
}

const SOURCE_LABELS: Record<string, string> = {
  as_is: "As-Is",
  approved_with_change: "Approved with Change",
};

/** PO Confirmed Report — read-only view of confirmed/approved PO lines,
 * showing the actually-confirmed qty/date (approvedQty/approvedDeliveryDate,
 * already reconciled server-side) rather than the stale original values. */
export function PoConfirmedReport() {
  const [vendorId, setVendorId] = useState("");
  const [plantId, setPlantId] = useState("");
  const [materialNumber, setMaterialNumber] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [erpId, setErpId] = useState("");
  const [dateFormat, setDateFormat] = useState("DD/MMM/YYYY");

  const { data: vendors } = useVendors();
  const { data: plants } = usePlants();
  const { data: erpSystems } = useErpSystems();

  const vendorMap = useMemo(() => new Map((vendors ?? []).map((v) => [v.id, v.vendorNumber])), [vendors]);
  const plantMap = useMemo(() => new Map((plants ?? []).map((p) => [p.id, p.plantNumber])), [plants]);

  const materialNumberDebounced = useDebounce(materialNumber);

  const query = useQuery({
    queryKey: ["po-confirmed-report", vendorId, plantId, materialNumberDebounced, fromDate, toDate, erpId],
    queryFn: () =>
      api.get<{ rows: PoConfirmedRow[] }>(
        `/po/confirmed-report?${new URLSearchParams({
          ...(vendorId && { vendorId }),
          ...(plantId && { plantId }),
          ...(materialNumberDebounced && { materialNumber: materialNumberDebounced }),
          ...(fromDate && { fromDate }),
          ...(toDate && { toDate }),
          ...(erpId && { erpId }),
        })}`
      ),
  });

  const columns = useMemo<ReportColumn<PoConfirmedRow>[]>(
    () => [
      { key: "creationDate", label: "Creation Date", render: (r) => formatDate(r.creationDate, dateFormat) },
      { key: "poNumber", label: "PO Number" },
      { key: "poLineItem", label: "PO Line", align: "right" },
      { key: "materialNumber", label: "Material" },
      { key: "materialDescription", label: "Description" },
      { key: "approvedQty", label: "Approved Qty", align: "right", render: (r) => r.approvedQty ?? "—" },
      { key: "unit", label: "Unit" },
      { key: "originalDeliveryDate", label: "Original Delivery Date", render: (r) => formatDate(r.originalDeliveryDate, dateFormat) },
      { key: "approvedDeliveryDate", label: "Approved Delivery Date", render: (r) => formatDate(r.approvedDeliveryDate, dateFormat) },
      { key: "etdDate", label: "ETD Date", render: (r) => formatDate(r.etdDate, dateFormat) },
      { key: "plantId", label: "Plant", render: (r) => plantMap.get(r.plantId) ?? r.plantId },
      { key: "unitPrice", label: "Unit Price", align: "right", render: (r) => r.unitPrice?.toFixed(2) ?? "—" },
      { key: "currency", label: "Currency" },
      { key: "vendorId", label: "Vendor", render: (r) => vendorMap.get(r.vendorId) ?? r.vendorId },
      { key: "paymentTerms", label: "Payment Terms" },
      { key: "mrpControllerNumber", label: "MRP Cont. #" },
      { key: "mrpControllerName", label: "MRP Cont. Name" },
      { key: "regionalBuyer", label: "Regional Buyer" },
      { key: "regionalBuyerName", label: "Regional Buyer Name" },
      { key: "confirmationSource", label: "Source", render: (r) => (r.confirmationSource ? SOURCE_LABELS[r.confirmationSource] ?? r.confirmationSource : "—") },
    ],
    [dateFormat, plantMap, vendorMap]
  );

  return (
    <div>
      <Card className="mb-5">
        <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
          <FieldSelect
            label="Vendor Number"
            value={vendorId}
            onChange={(e) => setVendorId(e.target.value)}
            options={[{ value: "", label: "All vendors" }, ...(vendors ?? []).map((v) => ({ value: v.id, label: `${v.vendorNumber} - ${v.name}` }))]}
          />
          <FieldSelect
            label="Plant Number"
            value={plantId}
            onChange={(e) => setPlantId(e.target.value)}
            options={[{ value: "", label: "All plants" }, ...(plants ?? []).map((p) => ({ value: p.id, label: `${p.plantNumber} - ${p.name}` }))]}
          />
          <FieldInput label="Material Number" value={materialNumber} onChange={(e) => setMaterialNumber(e.target.value)} />
          <FieldSelect
            label="ERPID"
            value={erpId}
            onChange={(e) => setErpId(e.target.value)}
            options={[{ value: "", label: "All ERP systems" }, ...(erpSystems ?? []).map((e) => ({ value: e.name, label: e.name }))]}
          />
          <div>
            <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">Creation Date Range</label>
            <div className="mt-1 flex gap-2">
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="text-sm border border-slate-200 dark:border-white/10 rounded-lg px-2 py-2 bg-white dark:bg-white/5 w-1/2" />
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="text-sm border border-slate-200 dark:border-white/10 rounded-lg px-2 py-2 bg-white dark:bg-white/5 w-1/2" />
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <div className="p-4 flex flex-wrap items-center gap-3 border-b border-slate-100 dark:border-white/10">
          <span className="font-semibold text-sm shrink-0">PO Confirmed Report</span>
          <div className="shrink-0">
            <DateFormatRadios value={dateFormat} onChange={setDateFormat} />
          </div>
          <div className="ml-auto flex gap-2 items-center">
            <button
              onClick={() => query.refetch()}
              disabled={query.isFetching}
              title="Refresh"
              aria-label="Refresh"
              className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-white/5 outline-none focus-visible:ring-2 focus-visible:ring-sbd-yellow/60 disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${query.isFetching ? "animate-spin" : ""}`} />
            </button>
            <Button variant="outline" icon={Download} onClick={() => api.download("/po/export/confirmed", "PO_Confirmed_Report.xlsx")}>
              Export to Excel
            </Button>
          </div>
        </div>
        <ReportTable columns={columns} rows={query.data?.rows ?? []} rowKey={(r) => r.id} loading={query.isLoading} emptyMessage="No confirmed PO lines match this filter." />
      </Card>
    </div>
  );
}
